import type {
  ApiResponse,
  ActivityItem,
  BlogPost,
  SyncStatus,
  SourceCounts,
  TagCount,
  Page,
} from '@/types';

const API_BASE = '/api';

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP error ${response.status}`, response.status);
  }

  return response.json();
}

/**
 * Activity Feed API
 */
export const feedApi = {
  /**
   * Get activity feed with optional filters
   */
  async getRecent(options?: {
    source?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ActivityItem[]> {
    const params = new URLSearchParams();
    if (options?.source) params.set('source', options.source);
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    const response = await fetchApi<ApiResponse<ActivityItem[]>>(
      `/feed${query ? `?${query}` : ''}`
    );
    return response.data || [];
  },

  /**
   * Get source counts
   */
  async getSourceCounts(): Promise<SourceCounts> {
    const response = await fetchApi<ApiResponse<SourceCounts>>('/feed/sources');
    return response.data || {};
  },

  /**
   * Get single activity item
   */
  async getById(id: string): Promise<ActivityItem | null> {
    const response = await fetchApi<ApiResponse<ActivityItem>>(`/feed/${id}`);
    return response.data || null;
  },
};

/**
 * Blog API
 */
export const blogApi = {
  /**
   * List blog posts
   */
  async list(options?: {
    tag?: string;
    limit?: number;
    offset?: number;
  }): Promise<BlogPost[]> {
    const params = new URLSearchParams();
    if (options?.tag) params.set('tag', options.tag);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    const response = await fetchApi<ApiResponse<BlogPost[]>>(
      `/blog${query ? `?${query}` : ''}`
    );
    return response.data || [];
  },

  /**
   * Get blog post by slug
   */
  async getBySlug(slug: string): Promise<BlogPost | null> {
    const response = await fetchApi<ApiResponse<BlogPost>>(`/blog/${slug}`);
    return response.data || null;
  },

  /**
   * Get all tags
   */
  async getTags(): Promise<TagCount[]> {
    const response = await fetchApi<ApiResponse<TagCount[]>>('/blog/tags');
    return response.data || [];
  },
};

/**
 * Sync API
 */
export const syncApi = {
  /**
   * Get sync status
   */
  async getStatus(): Promise<SyncStatus> {
    const response = await fetchApi<ApiResponse<SyncStatus>>('/sync/status');
    return response.data || {};
  },
};

/**
 * Pages API
 */
export const pagesApi = {
  /**
   * List all pages
   */
  async getAll(): Promise<Page[]> {
    const response = await fetchApi<ApiResponse<Page[]>>('/pages');
    return response.data || [];
  },

  /**
   * Get page by slug
   */
  async getBySlug(slug: string): Promise<Page | null> {
    const response = await fetchApi<ApiResponse<Page>>(`/pages/${slug}`);
    return response.data || null;
  },
};

// Removed unused getDefaultTheme function
